import { Box, Chip, Grid, Stack, Typography } from "@mui/material"
import { format } from "date-fns"
import React from "react"
import MD from "react-markdown"

import FourOhFour from "../../pages/404"
import { getEnvironmentVariable } from "../../util/env"
import { containerCSS } from "../../util"
import BreadCrumb from "../BreadCrumb"
import Link, { NextLinkComposed } from "../Link"
import OptimizedImage from "../OptimizedImage"
import Seo from "../SEO"
import { getComponent } from "."

const BlogPost = ({ preview, ...props }) => {
    let { content, breadcrumbs, includeLayout = true } = props

    if (!content) {
        return <FourOhFour />
    }

    const { sys: { locale } = {} } = content || {}

    let {
        blocks,
        category,
        heading,
        image,
        imageSubText,
        author,
        body,
        date,
        path,
        seo = {},
        blocksCollection = {},
    } = content.fields || content
    blocks = blocks || blocksCollection.items || []
    seo = seo?.fields || seo
    image = image?.fields || image
    const imageFile = image?.file?.url

    const hideBreadcrumbs = breadcrumbs && !breadcrumbs.length

    const linkStyles = {
        padding: "0 2px",
    }

    const SITE_URL = getEnvironmentVariable({ key: "SITE_URL" })

    const socialMediaLinks = (
        <>
            <Link
                href={`https://twitter.com/share?text=${heading}&url=${
                    SITE_URL + path
                }`}
                noLinkStyle
            >
                <i className={"fa-brands fa-twitter"} style={linkStyles} />
            </Link>
            <Link
                href={`https://www.facebook.com/sharer/sharer.php?u=${
                    SITE_URL + path
                }`}
                noLinkStyle
            >
                <i className={"fa-brands fa-facebook"} style={linkStyles} />
            </Link>
            <Link
                href={`http://pinterest.com/pin/create/button/?url=${
                    SITE_URL + path
                }`}
                noLinkStyle
            >
                <i className={"fa-brands fa-pinterest"} style={linkStyles} />
            </Link>
            <Link
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${
                    SITE_URL + path
                }`}
                noLinkStyle
            >
                <i className={"fa-brands fa-linkedin"} style={linkStyles} />
            </Link>
        </>
    )

    return (
        <>
            <Seo
                title={seo.title}
                description={seo.description}
                noIndex={seo.noindex}
                noFollow={seo.nofollow}
                shareImage={imageFile ? `https:${imageFile}` : undefined}
                schema={seo.schema}
            />

            {/* unless passed in props, navigation will be based on the URL paths */}
            {includeLayout && !hideBreadcrumbs && (
                <BreadCrumb navigations={breadcrumbs} />
            )}

            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                    containerCSS,
                    maxWidth: { md: "75rem" },
                    p: { xs: "1.5rem", md: "1.5rem 0rem" },
                }}
            >
                <Typography variant="h1">{heading}</Typography>

                <Typography
                    align="left"
                    sx={{
                        width: "100%",
                        marginBottom: "2.7rem",
                    }}
                >
                    {format(new Date(date), "PPP")}{" "}
                    {author ? `| ${author} |` : " | "} {socialMediaLinks}
                </Typography>

                <Box
                    sx={{
                        boxSizing: "border-box",
                        maxWidth: "40rem",
                        width: "100%",
                    }}
                >
                    <OptimizedImage
                        src={imageFile}
                        containerStyle={{
                            display: "contents",
                        }}
                    />
                    {imageSubText ? (
                        <Typography
                            variant="body3"
                            sx={{
                                fontStyle: "italic",
                            }}
                        >
                            {imageSubText}
                        </Typography>
                    ) : null}
                </Box>

                <Typography component="span" variant="body1">
                    <MD
                        components={{
                            a: ({ node, ...props }) => {
                                return <Link {...props} underline="hover" />
                            },
                        }}
                    >
                        {body}
                    </MD>
                </Typography>

                {blocks && blocks?.length > 0 && (
                    <Grid container>
                        {blocks.map((block, index) => {
                            const TypeComponent = getComponent(block)

                            return (
                                TypeComponent && (
                                    <React.Fragment key={`block-${index}`}>
                                        <TypeComponent
                                            {...{ ...block.fields, locale }}
                                        />
                                    </React.Fragment>
                                )
                            )
                        })}
                    </Grid>
                )}

                {category && category?.length > 0 && (
                    <Grid
                        container
                        columnSpacing={2}
                        sx={{ pt: 2, width: "100%" }}
                    >
                        {category.map((item, index) => (
                            <Grid item key={`${item}-${index}`}>
                                <Chip
                                    clickable
                                    component={NextLinkComposed}
                                    label={item}
                                    to={{
                                        pathname: "/blog",
                                        query: {
                                            category: item.toLowerCase(),
                                        },
                                    }}
                                    variant="outlined"
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Stack>
        </>
    )
}

export default BlogPost
